/* eslint-disable react/prop-types */
// item/items are the shared catalog shape used throughout src/lib/db.js,
// not re-declared per component.
import React, { useMemo, useState } from "react";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import { useProducts } from "lib/useProducts";
import { PRODUCT_CATEGORIES } from "lib/db";

function orderCategories(present) {
  const canonical = PRODUCT_CATEGORIES.filter((c) => present.includes(c));
  const extra = present.filter((c) => !PRODUCT_CATEGORIES.includes(c)).sort();
  return [...canonical, ...extra];
}

function ProductCard({ item }) {
  const [isActive, setIsActive] = useState(false);

  return (
    <div
      className={`product-card group relative overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-md transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl ${
        isActive ? "is-active" : ""
      }`}
    >
      <button
        type="button"
        className="relative block h-64 w-full bg-slate-50"
        onClick={() => setIsActive((prev) => !prev)}
        aria-expanded={isActive}
        aria-label={`${item.title} packaging options`}
      >
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-contain p-3"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-300">No image yet</div>
        )}
      </button>
      <div className="border-t border-slate-100 p-4">
        <h3 className="text-center font-semibold text-slate-800">{item.title}</h3>
        <p className="mt-1 text-center text-sm text-slate-400">{item.type}</p>
      </div>

      {item.packaging?.length > 0 && (
        <div className="packaging-overlay absolute inset-0 flex flex-col items-center justify-center gap-2 bg-maroon-900/90 p-4 text-center transition-opacity duration-300">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gold-300">
            Available Packaging
          </p>
          <ul className="space-y-1 text-white">
            {item.packaging.map((pack) => (
              <li key={pack} className="text-lg">
                {pack}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setIsActive(false)}
            className="mt-3 rounded-full border border-white/30 px-4 py-1 text-xs text-white/80 sm:hidden"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}

function ProductGrid({ items }) {
  if (items.length === 0) {
    return (
      <p className="py-16 text-center text-slate-400">No products in this category yet.</p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <ProductCard item={item} key={item.id} />
      ))}
    </div>
  );
}

export default function AllPortfolio() {
  const { products, isLoading, error } = useProducts();
  const [showSummary, setShowSummary] = useState(false);

  const categories = useMemo(
    () => orderCategories(Array.from(new Set(products.map((p) => p.type)))),
    [products],
  );
  const [activeCategory, setActiveCategory] = useState(null);
  const currentCategory = activeCategory && categories.includes(activeCategory)
    ? activeCategory
    : categories[0];

  const byCategory = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      map[cat] = products.filter((p) => p.type === cat);
    });
    return map;
  }, [products, categories]);

  if (isLoading) {
    return (
      <div className="container mx-auto p-4">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-72 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <p className="container mx-auto p-4 text-center text-red-600">{error}</p>;
  }

  if (categories.length === 0) {
    return <p className="container mx-auto p-4 text-center text-slate-400">No products yet.</p>;
  }

  return (
    <div className="container mx-auto p-4">
      <Tabs
        className="flex flex-col"
        selectedIndex={categories.indexOf(currentCategory)}
        onSelect={(index) => setActiveCategory(categories[index])}
      >
        <TabList className="mb-8 flex flex-wrap justify-center gap-3">
          {categories.map((cat) => (
            <Tab key={cat} className="cursor-pointer rounded-full outline-none">
              <span
                className={`inline-block rounded-full border-2 px-6 py-2 font-medium transition-colors ${
                  cat === currentCategory
                    ? "border-maroon-700 bg-maroon-700 text-white"
                    : "border-maroon-200 text-maroon-700 hover:bg-maroon-50"
                }`}
              >
                {cat}
              </span>
            </Tab>
          ))}
        </TabList>

        <button
          type="button"
          onClick={() => setShowSummary((prev) => !prev)}
          className="mx-auto mb-8 rounded-lg bg-maroon-50 px-6 py-2 font-medium text-maroon-700 transition-colors hover:bg-maroon-100"
        >
          {showSummary ? "Hide Summary" : "Show All Product Summary"}
        </button>

        {showSummary && (
          <div className="mb-8 overflow-x-auto">
            <table className="w-full table-auto border-collapse overflow-hidden rounded-xl border border-slate-200 text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="border-b border-slate-200 px-4 py-3 text-left font-medium text-slate-500">
                    Sr No
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left font-medium text-slate-500">
                    Product Name
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left font-medium text-slate-500">
                    Packaging Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {byCategory[currentCategory].map((item, index) => (
                  <tr key={item.id} className={index % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                    <td className="border-b border-slate-100 px-4 py-2">{index + 1}</td>
                    <td className="border-b border-slate-100 px-4 py-2">{item.title}</td>
                    <td className="border-b border-slate-100 px-4 py-2">
                      {item.packaging?.length ? item.packaging.join(", ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {categories.map((cat) => (
          <TabPanel key={cat}>
            <ProductGrid items={byCategory[cat]} />
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
